import { useTranslation } from 'react-i18next';
import { formatDateSafe, compareDateStrings } from '../../lib/dateUtils';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Plus, CreditCard as Edit2, Trash2, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, X, Loader as Loader2, Receipt, Search, Upload, ArrowUpDown, ArrowUp, ArrowDown, Trash, FileText, RefreshCw, TrendingUp, ArrowLeftRight, EyeOff, Info, AlarmClock, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useAuth } from '../../features/auth';
import { useCompany } from '../../context/CompanyContext';
import { usePermissions } from '../../hooks/usePermissions';
import { PeriodFilter, getPresetRange } from '../../components/cashflow/PeriodFilter';
import type { PeriodPreset, DateRange } from '../../components/cashflow/PeriodFilter';
import { emitToast } from '../../components/ui/Toast';
import { bankTransactionsService, CreateBankTransactionData } from '../../lib/bankTransactionsService';
import { bankAccountsService } from '../../lib/bankAccountsService';
import { supabase } from '../../lib/supabase';
import { accountingCategoriesService } from '../../lib/accountingCategoriesService';
import { recurringActionsService, RecurringActionLineInsert } from '../../lib/recurringActionsService';
import { knownActionsService, KnownAction, KnownActionInsert } from '../../lib/knownActionsService';
import { offsetCodesService } from '../../lib/offsetCodesService';
import { buildOffsetMap } from '../../lib/offsetCalculator';
import type { OffsetResult } from '../../lib/offsetCalculator';
import { isRolling, isSnoozed, getEffectiveDate, getRolloverLabel } from '../../lib/rolloverUtils';
import { RolloverSnoozePopover } from '../../components/cashflow/RolloverSnoozePopover';
import { BankTransaction, BankAccount, AccountingCategory, Company, CashflowMapping, RecurringActionLine, OffsetCode } from '../../types/database';

import { ImportTransactionsModal } from '../../components/modals/ImportTransactionsModal';
import { AddRecurringActionModal } from '../../components/modals/AddRecurringActionModal';
import type { RecurringPrefillData } from '../../components/modals/AddRecurringActionModal';
import { EditRecurringActionModal } from '../../components/modals/EditRecurringActionModal';
import { BulkEditRecurringModal } from '../../components/modals/BulkEditRecurringModal';
import type { BulkEditFields } from '../../components/modals/BulkEditRecurringModal';
import { RecurringDiscoveryWizard } from '../../components/modals/RecurringDiscoveryWizard';
import { AddKnownActionModal } from '../../components/modals/AddKnownActionModal';
import { ImportKnownActionsModal } from '../../components/modals/ImportKnownActionsModal';
import { BankReconciliationView } from '../../components/reconciliation/BankReconciliationView';
import { MatchHistoryView } from '../../components/reconciliation/MatchHistoryView';
import { StatusIcon } from '../../components/common/StatusIcon';
import { CashflowForecastTable } from '../../components/cashflow/CashflowForecastTable';
import FinancyPullModal from '../../components/cashflow/FinancyPullModal';
import ImportReviewModal from '../../components/cashflow/ImportReviewModal';


type SortField = 'transaction_date' | 'description' | 'bank_account' | 'classification' | 'amount';
type SortDirection = 'asc' | 'desc';
type TabType = 'bank' | 'recurring' | 'known' | 'reconciliation' | 'cashflow' | 'matchHistory';

export function CashflowTablesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { activeCompanyId, activeCompany } = useCompany();

  // Tab state — default to the unified cashflow forecast view
  const [activeTab, setActiveTab] = useState<TabType>('cashflow');

  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState<Company | null>(null);
  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [accountingCategories, setAccountingCategories] = useState<AccountingCategory[]>([]);
  // userRole state removed — canEdit now derived from usePermissions() which
  // correctly handles Super Admins via isSuperAdmin bypass.
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<BankTransaction | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // ── Financy bank pull (Stage 1) ───────────────────────────────────────────
  /** Whether an active Financy integration exists for this company */
  const [financyConnected, setFinancyConnected] = useState(false);
  /** True while a "pull from bank" request is in flight */
  const [pullingBank, setPullingBank] = useState(false);
  /** Controls the controlled-pull modal (preview + confirm) */
  const [showFinancyModal, setShowFinancyModal] = useState(false);
  const [reviewTxnIds, setReviewTxnIds] = useState<string[]>([]);
  const [showReviewModal, setShowReviewModal] = useState(false);

  // Advanced features state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('transaction_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Bank-table extra filters
  /** When true, show only rows where accounting_category_id is null */
  const [showUnclassifiedOnly, setShowUnclassifiedOnly] = useState(false);

  // ── Period filters — default to current quarter for all four tables ────────
  const _currentQtr = getPresetRange('curr_quarter');

  /** Bank table period filter */
  const [filterDateFrom,    setFilterDateFrom]    = useState(_currentQtr.from);
  const [filterDateTo,      setFilterDateTo]      = useState(_currentQtr.to);
  const [bankPreset,        setBankPreset]        = useState<PeriodPreset | null>('curr_quarter');

  /** Recurring table period filter */
  const [recurringDateFrom, setRecurringDateFrom] = useState(_currentQtr.from);
  const [recurringDateTo,   setRecurringDateTo]   = useState(_currentQtr.to);
  const [recurringPreset,   setRecurringPreset]   = useState<PeriodPreset | null>('curr_quarter');

  /** Known actions period filter */
  const [knownDateFrom,     setKnownDateFrom]     = useState(_currentQtr.from);
  const [knownDateTo,       setKnownDateTo]       = useState(_currentQtr.to);
  const [knownPreset,       setKnownPreset]       = useState<PeriodPreset | null>('curr_quarter');

  /** Cashflow forecast period filter */
  const [cashflowDateFrom,  setCashflowDateFrom]  = useState(_currentQtr.from);
  const [cashflowDateTo,    setCashflowDateTo]    = useState(_currentQtr.to);
  const [cashflowPreset,    setCashflowPreset]    = useState<PeriodPreset | null>('curr_quarter');
  /** IDs of rows currently being saved by the inline category dropdown */
  const [savingCategoryIds, setSavingCategoryIds] = useState<Set<string>>(new Set());

  // ── Inline description edit state ─────────────────────────────────────────
  /** ID of the row whose description cell is currently open for editing */
  const [editingDescId,    setEditingDescId]    = useState<string | null>(null);
  /** Live value of the description input while editing */
  const [editingDescValue, setEditingDescValue] = useState('');
  /** IDs of rows whose description is currently being persisted to the DB */
  const [savingDescIds,    setSavingDescIds]    = useState<Set<string>>(new Set());

  // Recurring actions state
  const [recurringActions, setRecurringActions] = useState<RecurringActionLine[]>([]);
  const [recurringLoading, setRecurringLoading] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [showEditRecurringModal, setShowEditRecurringModal] = useState(false);
  const [editingRecurringLine, setEditingRecurringLine] = useState<RecurringActionLine | null>(null);
  const [savingRecurring, setSavingRecurring] = useState(false);
  const [showDiscoveryWizard, setShowDiscoveryWizard] = useState(false);
  const [recurringPrefill, setRecurringPrefill] = useState<RecurringPrefillData | null>(null);
  const [recurringSearchQuery, setRecurringSearchQuery] = useState('');
  const [recurringSortField, setRecurringSortField] = useState<'action_date' | 'description' | 'bank_account' | 'accounting_category' | 'amount'>('action_date');
  const [recurringSortDirection, setRecurringSortDirection] = useState<SortDirection>('desc');
  const [recurringSelectedIds, setRecurringSelectedIds] = useState<Set<string>>(new Set());
  const [bulkEditOpen,         setBulkEditOpen]         = useState(false);
  const [bulkEditSaving,       setBulkEditSaving]       = useState(false);

  // Offset codes state (loaded once alongside recurring/known; used for offset calculation)
  const [offsetCodes, setOffsetCodes] = useState<OffsetCode[]>([]);

  // Known actions state
  const [knownActions, setKnownActions] = useState<KnownAction[]>([]);
  const [knownLoading, setKnownLoading] = useState(false);
  const [showKnownModal, setShowKnownModal] = useState(false);
  const [showImportKnownModal, setShowImportKnownModal] = useState(false);
  const [editingKnownAction, setEditingKnownAction] = useState<KnownAction | null>(null);
  const [savingKnown, setSavingKnown] = useState(false);
  const [knownSearchQuery, setKnownSearchQuery] = useState('');
  const [knownSortField, setKnownSortField] = useState<'action_date' | 'description' | 'bank_account' | 'accounting_category' | 'amount'>('action_date');
  const [knownSortDirection, setKnownSortDirection] = useState<SortDirection>('desc');
  const [knownSelectedIds, setKnownSelectedIds] = useState<Set<string>>(new Set());

  // ── Snooze popover state ─────────────────────────────────────────────────
  // Tracks which row (if any) has its snooze popover open.
  // { id, sourceTable, currentSnooze, originalDate }
  const [snoozeTarget, setSnoozeTarget] = useState<{
    id: string;
    sourceTable: 'known' | 'recurring';
    currentSnooze: string | null;
    originalDate: string;
  } | null>(null);

  // Form state - now using accounting_category_id instead of classification
  const [formData, setFormData] = useState<{
    transaction_date: string;
    description: string;
    bank_account_id: string;
    accounting_category_id: string;
    amount: string;
  }>({
    transaction_date: new Date().toISOString().split('T')[0] || '',
    description: '',
    bank_account_id: '',
    accounting_category_id: '',
    amount: '',
  });

  useEffect(() => {
    if (activeCompanyId) {
      loadData();
    }
  }, [user, activeCompanyId]);

  const loadData = async () => {
    if (!user || !activeCompanyId) return;

    setLoading(true);
    setError(null);

    try {
      // Use activeCompanyId from global CompanyContext
      const companyId = activeCompanyId;

      // Set the company object from context (for display purposes)
      if (activeCompany) {
        setCompany(activeCompany as Company);
      }

      // Get bank accounts
      const { data: accountsData, error: accountsError } = await bankAccountsService.getBankAccounts(companyId);
      if (accountsError) {
        console.error('Load bank accounts error:', accountsError);
      } else {
        setBankAccounts(accountsData || []);
      }

      // Get accounting categories
      const { data: categoriesData, error: categoriesError } = await accountingCategoriesService.getAccountingCategories(companyId);
      if (categoriesError) {
        console.error('Load accounting categories error:', categoriesError);
      } else {
        setAccountingCategories(categoriesData || []);
      }

      // Get transactions
      const { data: transactionsData, error: transactionsError } = await bankTransactionsService.getBankTransactions(companyId);
      if (transactionsError) {
        console.error('Load transactions error:', transactionsError);
        setError(t('cashflow.tables.messages.loadError'));
        return;
      }

      setTransactions(transactionsData || []);

      // Check whether an active Financy integration exists (Stage 1 bank pull)
      const { data: financyIntegration } = await supabase
        .from('company_integrations')
        .select('is_active')
        .eq('company_id', companyId)
        .eq('provider_name', 'financy')
        .maybeSingle();
      setFinancyConnected(!!financyIntegration?.is_active);
    } catch (err) {
      console.error('Load data error:', err);
      setError(t('cashflow.tables.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleFinancyPull = async () => {
    if (!activeCompanyId || pullingBank) return;
    setPullingBank(true);
    setError(null);
    setSuccess(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/financy-sync-bank`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ company_id: activeCompanyId }),
        },
      );
      const result = await res.json().catch(() => null);
      if (!res.ok || !result || result.error) {
        setError(result?.error || `משיכת הנתונים מהבנק נכשלה (${res.status})`);
        return;
      }
      const imported = Number(result.imported ?? 0);
      setSuccess(
        imported > 0
          ? `נמשכו ${imported} תנועות חדשות מהבנק לטבלת הבנק.`
          : 'טבלת הבנק מעודכנת — לא נמצאו תנועות חדשות למשיכה.',
      );
      await loadData();
    } catch (err) {
      console.error('Financy pull error:', err);
      setError('משיכת הנתונים מהבנק נכשלה. נסו שוב מאוחר יותר.');
    } finally {
      setPullingBank(false);
    }
  };

  const loadRecurringActions = async () => {
    if (!activeCompanyId) return;

    setRecurringLoading(true);
    try {
      const { data, error } = await recurringActionsService.getRecurringActions(activeCompanyId);
      if (error) {
        console.error('Load recurring actions error:', error);
      } else {
        setRecurringActions(data || []);
      }
    } catch (err) {
      console.error('Load recurring actions error:', err);
    } finally {
      setRecurringLoading(false);
    }
  };

  const loadKnownActions = async () => {
    if (!activeCompanyId) return;

    setKnownLoading(true);
    try {
      const { data, error} = await knownActionsService.getKnownActions(activeCompanyId);
      if (error) {
        console.error('Load known actions error:', error);
      } else {
        setKnownActions(data || []);
      }
    } catch (err) {
      console.error('Load known actions error:', err);
    } finally {
      setKnownLoading(false);
    }
  };

  /** Load offset codes once — they rarely change and are needed for the offset
   *  calculation on both the recurring and known tabs. */
  const loadOffsetCodes = async () => {
    if (!activeCompanyId) return;
    try {
      const { data } = await offsetCodesService.getOffsetCodes(activeCompanyId);
      setOffsetCodes(data || []);
    } catch (err) {
      console.error('Load offset codes error:', err);
    }
  };

  // Load recurring actions when switching to recurring tab
  useEffect(() => {
    if (activeTab === 'recurring' && activeCompanyId) {
      loadRecurringActions();
      loadOffsetCodes();
    }
  }, [activeTab, activeCompanyId]);

  // Load known actions when switching to known tab
  useEffect(() => {
    if (activeTab === 'known' && activeCompanyId) {
      loadKnownActions();
      loadOffsetCodes();
    }
  }, [activeTab, activeCompanyId]);

  // ── canEdit via usePermissions — correctly handles Super Admin ─────────────
  // The old `userRole === 'admin' || userRole === 'member'` check failed for
  // Super Admins because they are not in company_members, so teamService returns
  // no matching row and userRole stays null.  usePermissions() uses isSuperAdmin
  // (profile.plan_tier === 'admin') which bypasses the member lookup entirely.
  const { isSuperAdmin, canEditCashflow } = usePermissions();
  const canEdit = isSuperAdmin || canEditCashflow;

  // ── DEBUG: log permission state whenever it changes ──────────────────────
  useEffect(() => {
    console.log('[CashflowTables] Permissions Check:', { isSuperAdmin, canEditCashflow, canEdit });
  }, [isSuperAdmin, canEditCashflow, canEdit]);

  // formatDate — timezone-safe display helper.
  // Delegates to formatDateSafe (src/lib/dateUtils.ts) which splits the
  // ISO string directly instead of constructing a Date object, so the
  // calendar date never shifts in negative-offset timezones (e.g. EST/EDT).
  // IMPORTANT: defined here (before useMemo blocks) to avoid TDZ crash.
  const formatDate = (dateString: string) => formatDateSafe(dateString);

  // Search and filter logic
  const filteredTransactions = useMemo(() => {
    let filtered = [...transactions];

    // Apply search filter
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter((transaction) => {
        const dateRaw        = (transaction.transaction_date ?? '').toLowerCase();
        const description    = (transaction.description ?? '').toLowerCase();
        const bankAccount    = (transaction.bank_accounts?.nickname ?? '').toLowerCase();
        const classification = t(`cashflow.tables.classifications.${transaction.classification}`).toLowerCase();
        const amount         = String(transaction.amount ?? '');

        return (
          dateRaw.includes(q)        ||
          description.includes(q)    ||
          bankAccount.includes(q)    ||
          classification.includes(q) ||
          amount.includes(q)
        );
      });
    }

    // Show-unclassified-only toggle
    if (showUnclassifiedOnly) {
      filtered = filtered.filter((tx) => !tx.accounting_category_id);
    }

    // Date-range filter
    if (filterDateFrom) {
      filtered = filtered.filter((tx) => tx.transaction_date >= filterDateFrom);
    }
    if (filterDateTo) {
      filtered = filtered.filter((tx) => tx.transaction_date <= filterDateTo);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let compareValue = 0;

      switch (sortField) {
        case 'transaction_date':
          compareValue = compareDateStrings(a.transaction_date, b.transaction_date);
          break;
        case 'description':
          compareValue = (a.description ?? '').localeCompare(b.description ?? '');
          break;
        case 'bank_account':
          compareValue = (a.bank_accounts?.nickname || '').localeCompare(b.bank_accounts?.nickname || '');
          break;
        case 'classification':
          compareValue = (a.classification || '').localeCompare(b.classification || '');
          break;
        case 'amount':
          compareValue = (a.amount ?? 0) - (b.amount ?? 0);
          break;
      }

      return sortDirection === 'asc' ? compareValue : -compareValue;
    });

    return filtered;
  }, [transactions, searchQuery, showUnclassifiedOnly, filterDateFrom, filterDateTo, sortField, sortDirection, t]);

  // Filtered and sorted recurring actions
  const filteredRecurringActions = useMemo(() => {
    let filtered = [...recurringActions];

    // Apply search filter — case-insensitive match across all visible fields.
    // Every field access is null-guarded so a bad row never silently aborts
    // the entire filter pass and leaves the table unfiltered.
    const q = recurringSearchQuery.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter((action) => {
        // Use the raw ISO date string for date matching (avoids formatDate call
        // inside memo and prevents any formatting-related silent exceptions).
        const dateRaw  = (action.action_date ?? '').toLowerCase();
        const desc     = (action.description ?? '').toLowerCase();
        const bank     = (action.bank_accounts?.nickname ?? '').toLowerCase();
        const category = (action.accounting_categories?.name ?? '').toLowerCase();
        const amount   = String(action.amount ?? '');

        return (
          dateRaw.includes(q)  ||
          desc.includes(q)     ||
          bank.includes(q)     ||
          category.includes(q) ||
          amount.includes(q)
        );
      });
    }

    // Date-range filter — compare against effective date (rolled-over), not raw action_date
    if (recurringDateFrom) filtered = filtered.filter(a => getEffectiveDate(a) >= recurringDateFrom);
    if (recurringDateTo)   filtered = filtered.filter(a => getEffectiveDate(a) <= recurringDateTo);

    // Apply sorting
    filtered.sort((a, b) => {
      let compareValue = 0;

      switch (recurringSortField) {
        case 'action_date':
          compareValue = compareDateStrings(a.action_date, b.action_date);
          break;
        case 'description':
          compareValue = (a.description ?? '').localeCompare(b.description ?? '');
          break;
        case 'bank_account':
          compareValue = (a.bank_accounts?.nickname || '').localeCompare(b.bank_accounts?.nickname || '');
          break;
        case 'accounting_category':
          compareValue = (a.accounting_categories?.name || '').localeCompare(b.accounting_categories?.name || '');
          break;
        case 'amount':
          compareValue = (a.amount ?? 0) - (b.amount ?? 0);
          break;
      }

      return recurringSortDirection === 'asc' ? compareValue : -compareValue;
    });

    return filtered;
  }, [recurringActions, recurringSearchQuery, recurringDateFrom, recurringDateTo, recurringSortField, recurringSortDirection]);

  const filteredKnownActions = useMemo(() => {
    let filtered = [...knownActions];

    // Apply search filter — same null-guarded pattern as filteredRecurringActions
    const q = knownSearchQuery.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter((action) => {
        const dateRaw  = (action.action_date ?? '').toLowerCase();
        const desc     = (action.description ?? '').toLowerCase();
        const bank     = (action.bank_accounts?.nickname ?? '').toLowerCase();
        const category = (action.accounting_categories?.name ?? '').toLowerCase();
        const amount   = String(action.amount ?? '');

        return (
          dateRaw.includes(q)  ||
          desc.includes(q)     ||
          bank.includes(q)     ||
          category.includes(q) ||
          amount.includes(q)
        );
      });
    }

    // Date-range filter — compare against effective date (rolled-over), not raw action_date
    if (knownDateFrom) filtered = filtered.filter(a => getEffectiveDate(a) >= knownDateFrom);
    if (knownDateTo)   filtered = filtered.filter(a => getEffectiveDate(a) <= knownDateTo);

    // Apply sorting
    filtered.sort((a, b) => {
      let compareValue = 0;

      switch (knownSortField) {
        case 'action_date':
          compareValue = compareDateStrings(a.action_date, b.action_date);
          break;
        case 'description':
          compareValue = (a.description ?? '').localeCompare(b.description ?? '');
          break;
        case 'bank_account':
          compareValue = (a.bank_accounts?.nickname || '').localeCompare(b.bank_accounts?.nickname || '');
          break;
        case 'accounting_category':
          compareValue = (a.accounting_categories?.name || '').localeCompare(b.accounting_categories?.name || '');
          break;
        case 'amount':
          compareValue = (a.amount ?? 0) - (b.amount ?? 0);
          break;
      }

      return knownSortDirection === 'asc' ? compareValue : -compareValue;
    });

    return filtered;
  }, [knownActions, knownSearchQuery, knownDateFrom, knownDateTo, knownSortField, knownSortDirection]);

  /**
   * offsetMap — keyed by recurring action id.
   * Recomputed whenever recurring actions, known actions, or offset codes change.
   * Pure function: no side effects, no async.
   */
  const offsetMap = useMemo(
    () => buildOffsetMap(recurringActions, knownActions, offsetCodes),
    [recurringActions, knownActions, offsetCodes],
  );

  /**
   * knownOffsetIds — set of KnownAction IDs that are referenced by at least
   * one active offset rule, so the known tab can show an "offset applied" badge.
   */
  const knownOffsetIds = useMemo<Set<string>>(() => {
    const ids = new Set<string>();
    offsetMap.forEach((result) => {
      result.matchedKnownIds.forEach((id) => ids.add(id));
    });
    return ids;
  }, [offsetMap]);

  const exportRecurringToExcel = () => {
    const rows = filteredRecurringActions.map((a) => ({
      'תאריך': formatDateSafe(a.action_date),
      'תיאור': a.description ?? '',
      'חשבון בנק': bankAccounts.find(b => b.id === a.bank_account_id)?.nickname ?? '',
      'קטגוריה': accountingCategories.find(c => c.id === a.accounting_category_id)?.name ?? '',
      'סכום': a.amount ?? 0,
      'סטטוס': a.status ?? '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'פעולות קבועות');
    XLSX.writeFile(wb, `פעולות_קבועות_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportKnownToExcel = () => {
    const rows = filteredKnownActions.map((a) => ({
      'תאריך': formatDateSafe(a.action_date),
      'תיאור': a.description ?? '',
      'חשבון בנק': bankAccounts.find(b => b.id === a.bank_account_id)?.nickname ?? '',
      'קטגוריה': accountingCategories.find(c => c.id === a.accounting_category_id)?.name ?? '',
      'סכום': a.amount ?? 0,
      'סטטוס': a.status ?? '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'פעולות ידועות');
    XLSX.writeFile(wb, `פעולות_ידועות_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4" />;
    }
    return sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredTransactions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredTransactions.map(t => t.id)));
    }
  };

  const handleSelectOne = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    const count = selectedIds.size;
    if (!window.confirm(t('cashflow.tables.messages.bulkDeleteConfirm', { count }))) {
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      const { error: deleteError } = await bankTransactionsService.bulkDeleteTransactions(
        Array.from(selectedIds)
      );

      if (deleteError) {
        setError(t('cashflow.tables.messages.bulkDeleteError'));
        return;
      }

      setSuccess(t('cashflow.tables.messages.bulkDeleteSuccess', { count }));
      setSelectedIds(new Set());
      await loadData();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Bulk delete error:', err);
      setError(t('cashflow.tables.messages.bulkDeleteError'));
    }
  };

  const handleImportSuccess = async (importedIds?: string[]) => {
    await loadData();
    if (importedIds && importedIds.length > 0) {
      setReviewTxnIds(importedIds);
      setShowReviewModal(true);
    } else {
      setSuccess(t('cashflow.tables.messages.uploadSuccess', { count: 1 }));
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const resetForm = () => {
    setFormData({
      transaction_date: new Date().toISOString().split('T')[0] || '',
      description: '',
      bank_account_id: bankAccounts.length > 0 && bankAccounts[0] ? bankAccounts[0].id : '',
      accounting_category_id: accountingCategories.length > 0 && accountingCategories[0] ? accountingCategories[0].id : '',
      amount: '',
    });
    setEditingTransaction(null);
  };

  const handleAddClick = () => {
    resetForm();
    setShowModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleEditClick = (transaction: BankTransaction) => {
    setEditingTransaction(transaction);
    setFormData({
      transaction_date: transaction.transaction_date,
      description: transaction.description,
      bank_account_id: transaction.bank_account_id,
      accounting_category_id: transaction.accounting_category_id || (accountingCategories[0]?.id || ''),
      amount: transaction.amount.toString(),
    });
    setShowModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!company) return;

    const amountValue = parseFloat(formData.amount);
    if (isNaN(amountValue)) {
      setError('Please enter a valid amount');
      return;
    }

    // Validate required accounting category
    if (!formData.accounting_category_id) {
      setError('חובה לבחור סיווג הנהלת חשבונות');
      emitToast('חובה לבחור סיווג הנהלת חשבונות', 'error');
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingTransaction) {
        const { error: updateError } = await bankTransactionsService.updateBankTransaction(
          editingTransaction.id,
          {
            transaction_date: formData.transaction_date,
            description: formData.description,
            bank_account_id: formData.bank_account_id,
            accounting_category_id: formData.accounting_category_id || null,
            amount: amountValue,
          }
        );

        if (updateError) {
          setError(t('cashflow.tables.messages.updateError'));
          return;
        }

        setSuccess(t('cashflow.tables.messages.updateSuccess'));
      } else {
        if (!activeCompanyId) {
          setError('Company not found');
          return;
        }

        const companyId = activeCompanyId;

        const transactionData: CreateBankTransactionData = {
          company_id: companyId,
          transaction_date: formData.transaction_date,
          description: formData.description,
          bank_account_id: formData.bank_account_id,
          accounting_category_id: formData.accounting_category_id || null,
          amount: amountValue,
        };

        const { error: createError } = await bankTransactionsService.createBankTransaction(transactionData);

        if (createError) {
          setError(t('cashflow.tables.messages.createError'));
          return;
        }

        setSuccess(t('cashflow.tables.messages.createSuccess'));
      }

      setShowModal(false);
      resetForm();
      await loadData();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Submit error:', err);
      setError(editingTransaction 
        ? t('cashflow.tables.messages.updateError')
        : t('cashflow.tables.messages.createError')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (transactionId: string) => {
    if (!window.confirm(t('cashflow.tables.messages.deleteConfirm'))) {
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      const { error: deleteError } = await bankTransactionsService.deleteBankTransaction(transactionId);

      if (deleteError) {
        setError(t('cashflow.tables.messages.deleteError'));
        return;
      }

      setSuccess(t('cashflow.tables.messages.deleteSuccess'));
      await loadData();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Delete error:', err);
      setError(t('cashflow.tables.messages.deleteError'));
    }
  };

  const handleToggleBankStatus = async (transactionId: string, currentStatus: string) => {
    setError(null);
    setSuccess(null);

    try {
      const { error: toggleError } = await bankTransactionsService.toggleTransactionStatus(transactionId, currentStatus);

      if (toggleError) {
        const errorMsg = typeof toggleError === 'object' && 'message' in toggleError 
          ? (toggleError as any).message 
          : 'שגיאה בשינוי סטטוס';
        setError(errorMsg);
        return;
      }

      setSuccess('הסטטוס עודכן בהצלחה');
      await loadData();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Toggle status error:', err);
      setError('שגיאה בשינוי סטטוס');
    }
  };

  /**
   * Inline category change — optimistic update.
   *
   * 1. Immediately patches the matching row in local `transactions` state so
   *    the table reflects the new category without any scroll-position reset.
   * 2. Persists to the DB in the background.
   * 3. On error, rolls the row back to its previous value and shows a toast.
   *
   * No `loadData()` call is made, so the table never re-mounts.
   */

  // ── Inline description handlers ───────────────────────────────────────────

  /** Open the description input for a given row. */
  const handleDescClick = useCallback((tx: BankTransaction) => {
    console.log('[CashflowTables] handleDescClick fired for row:', tx.id, '| desc:', tx.description);
    setEditingDescId(tx.id);
    setEditingDescValue(tx.description ?? '');
  }, []);

  /**
   * Commit the edited description.
   *  - Trims whitespace; if unchanged, just closes the editor (no DB call).
   *  - Optimistically updates local state, persists in background.
   *  - On error, rolls back and shows a toast.
   */
  const handleDescSave = useCallback(async (transactionId: string) => {
    // Close the editor immediately so no duplicate save fires on blur-after-enter
    setEditingDescId(null);

    const prevTx = transactions.find((t) => t.id === transactionId);
    if (!prevTx) return;

    const newDesc = editingDescValue.trim();
    // No-op if text is identical
    if (newDesc === (prevTx.description ?? '').trim()) return;

    // Optimistic UI update
    setTransactions((prev) =>
      prev.map((tx) =>
        tx.id === transactionId ? { ...tx, description: newDesc } : tx,
      ),
    );

    // Mark row as saving
    setSavingDescIds((prev) => new Set(prev).add(transactionId));

    const { error: updateErr } = await bankTransactionsService.updateBankTransaction(
      transactionId,
      { description: newDesc },
    );

    setSavingDescIds((prev) => {
      const next = new Set(prev);
      next.delete(transactionId);
      return next;
    });

    if (updateErr) {
      // Roll back
      setTransactions((prev) =>
        prev.map((tx) =>
          tx.id === transactionId ? { ...prevTx } : tx,
        ),
      );
      const msg =
        typeof updateErr === 'object' && updateErr !== null && 'message' in updateErr
          ? (updateErr as { message: string }).message
          : 'שגיאה בשמירת התיאור';
      emitToast(msg, 'error');
    }
  }, [transactions, editingDescValue]);

  /** Cancel editing without saving. */
  const handleDescCancel = useCallback(() => {
    setEditingDescId(null);
    setEditingDescValue('');
  }, []);

  const handleInlineCategoryChange = async (
    transactionId: string,
    newCategoryId: string,
  ) => {
    // Find the row and remember the old value for rollback
    const prevTx = transactions.find((t) => t.id === transactionId);
    if (!prevTx) return;

    const nextCategoryId = newCategoryId || null;

    // Resolve the full category object from the already-loaded list
    const nextCategory = nextCategoryId
      ? accountingCategories.find((c) => c.id === nextCategoryId) ?? null
      : null;

    // ── Optimistic patch ────────────────────────────────────────────────────
    setTransactions((prev) =>
      prev.map((tx) =>
        tx.id !== transactionId
          ? tx
          : {
              ...tx,
              accounting_category_id: nextCategoryId,
              accounting_categories: nextCategory
                ? { name: nextCategory.name, cashflow_mapping: nextCategory.cashflow_mapping }
                : undefined,
            },
      ),
    );

    // Mark this row as saving (shows a subtle spinner in the cell)
    setSavingCategoryIds((prev) => new Set(prev).add(transactionId));

    // ── Persist ─────────────────────────────────────────────────────────────
    const { error: updateErr } = await bankTransactionsService.updateBankTransaction(
      transactionId,
      { accounting_category_id: nextCategoryId },
    );

    setSavingCategoryIds((prev) => {
      const next = new Set(prev);
      next.delete(transactionId);
      return next;
    });

    if (updateErr) {
      // Roll back the optimistic patch
      setTransactions((prev) =>
        prev.map((tx) =>
          tx.id !== transactionId
            ? tx
            : { ...prevTx },   // restore entire previous object
        ),
      );
      const msg =
        typeof updateErr === 'object' && updateErr !== null && 'message' in updateErr
          ? (updateErr as { message: string }).message
          : 'שגיאה בעדכון הסיווג';
      emitToast(msg, 'error');
    }
  };

  const handleSaveRecurringAction = async (lines: RecurringActionLineInsert[]) => {
    if (!company) return;

    setSavingRecurring(true);
    setError(null);
    setSuccess(null);

    try {
      const { error: createError } = await recurringActionsService.bulkInsertLines(lines);

      if (createError) {
        setError(createError.message || 'שגיאה בשמירת פעולות קבועות');
        return;
      }

      setSuccess(`${lines.length} פעולות נשמרו בהצלחה`);
      setShowRecurringModal(false);
      await loadRecurringActions();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Save recurring actions error:', err);
      setError('שגיאה בשמירת פעולות קבועות');
    } finally {
      setSavingRecurring(false);
    }
  };

  // Recurring actions handlers
  const handleRecurringSort = (field: 'action_date' | 'description' | 'bank_account' | 'accounting_category' | 'amount') => {
    if (recurringSortField === field) {
      setRecurringSortDirection(recurringSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setRecurringSortField(field);
      setRecurringSortDirection('asc');
    }
  };

  const getRecurringSortIcon = (field: 'action_date' | 'description' | 'bank_account' | 'accounting_category' | 'amount') => {
    if (recurringSortField !== field) {
      return <ArrowUpDown className="h-4 w-4" />;
    }
    return recurringSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  const handleRecurringSelectAll = () => {
    if (recurringSelectedIds.size === filteredRecurringActions.length) {
      setRecurringSelectedIds(new Set());
    } else {
      setRecurringSelectedIds(new Set(filteredRecurringActions.map(a => a.id)));
    }
  };

  const handleRecurringSelectOne = (id: string) => {
    const newSelected = new Set(recurringSelectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setRecurringSelectedIds(newSelected);
  };

  const handleRecurringBulkDelete = async () => {
    if (recurringSelectedIds.size === 0) return;

    const count = recurringSelectedIds.size;
    if (!window.confirm(`האם אתה בטוח שברצונך למחוק ${count} פעולות?`)) {
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      const { error: deleteError } = await recurringActionsService.bulkDeleteLines(
        Array.from(recurringSelectedIds)
      );

      if (deleteError) {
        setError('שגיאה במחיקת פעולות');
        return;
      }

      setSuccess(`${count} פעולות נמחקו בהצלחה`);
      setRecurringSelectedIds(new Set());
      await loadRecurringActions();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Bulk delete recurring error:', err);
      setError('שגיאה במחיקת פעולות');
    }
  };

  const handleRecurringBulkCancelRollover = async () => {
    if (recurringSelectedIds.size === 0) return;

    const count = recurringSelectedIds.size;
    if (!window.confirm(`האם לבטל גלגול אוטומטי עבור ${count} פעולות נבחרות?`)) return;

    try {
      const { error } = await recurringActionsService.bulkCancelRollover(
        Array.from(recurringSelectedIds)
      );

      if (error) {
        emitToast('שגיאה בביטול גלגול הפעולות', 'error');
        return;
      }

      emitToast(`גלגול בוטל עבור ${count} פעולות`, 'success');
      setRecurringSelectedIds(new Set());
      await loadRecurringActions();
    } catch (err) {
      console.error('Bulk cancel rollover error:', err);
      emitToast('שגיאה בביטול גלגול הפעולות', 'error');
    }
  };

  /**
   * Bulk-edit handler — called by BulkEditRecurringModal when the user submits.
   *
   * Strategy:
   *  1. Build "shared" updates (description, amount, accounting_category_id)
   *     from whichever fields the user filled.
   *  2. For "day of month": read each selected row's current action_date,
   *     replace only the day portion, and include it in that row's update.
   *  3. Send all updates via recurringActionsService.bulkEditLines().
   */
  const handleRecurringBulkEdit = async (fields: BulkEditFields) => {
    if (recurringSelectedIds.size === 0) return;
    setBulkEditSaving(true);

    try {
      // Build the shared (non-date) patch, skipping empty fields
      const sharedPatch: Partial<{ description: string; amount: number; accounting_category_id: string }> = {};
      if (fields.description.trim() !== '') sharedPatch.description = fields.description.trim();
      if (fields.amount.trim() !== '') sharedPatch.amount = parseFloat(fields.amount);
      if (fields.accounting_category_id !== '') sharedPatch.accounting_category_id = fields.accounting_category_id;

      const newDay = fields.dayOfMonth.trim() !== '' ? parseInt(fields.dayOfMonth, 10) : null;

      // Resolve selected rows so we can read their existing action_date
      const selectedRowObjects = recurringActions.filter(r => recurringSelectedIds.has(r.id));

      const perRowUpdates = selectedRowObjects.map(row => {
        const rowPatch: Partial<{ description: string; amount: number; accounting_category_id: string; action_date: string }> = {
          ...sharedPatch,
        };

        if (newDay !== null) {
          // Parse existing YYYY-MM-DD, replace only the DD part
          const [yyyy, mm] = row.action_date.split('-');
          // Pad day; Date will clamp overflow days (e.g., Apr 31 → Apr 30)
          const candidate = new Date(
            parseInt(yyyy!, 10),
            parseInt(mm!, 10) - 1,  // month is 0-indexed
            newDay,
          );
          const clampedYear  = candidate.getFullYear().toString().padStart(4, '0');
          const clampedMonth = (candidate.getMonth() + 1).toString().padStart(2, '0');
          const clampedDay   = candidate.getDate().toString().padStart(2, '0');
          rowPatch.action_date = `${clampedYear}-${clampedMonth}-${clampedDay}`;
        }

        return { id: row.id, updates: rowPatch };
      });

      const { error } = await recurringActionsService.bulkEditLines(perRowUpdates);

      if (error) {
        emitToast('שגיאה בעדכון הפעולות הנבחרות', 'error');
        return;
      }

      emitToast(`${recurringSelectedIds.size} פעולות עודכנו בהצלחה`, 'success');
      setBulkEditOpen(false);
      setRecurringSelectedIds(new Set());
      await loadRecurringActions();
    } catch (err) {
      console.error('Bulk edit error:', err);
      emitToast('שגיאה בעדכון הפעולות הנבחרות', 'error');
    } finally {
      setBulkEditSaving(false);
    }
  };

  const handleRecurringDelete = async (id: string) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק פעולה זו?')) {
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      const { error: deleteError } = await recurringActionsService.deleteLine(id);

      if (deleteError) {
        setError('שגיאה במחיקת פעולה');
        return;
      }

      setSuccess('פעולה נמחקה בהצלחה');
      await loadRecurringActions();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Delete recurring error:', err);
      setError('שגיאה במחיקת פעולה');
    }
  };

  const handleToggleRecurringStatus = async (lineId: string, currentStatus: string) => {
    setError(null);
    setSuccess(null);

    try {
      const { error: toggleError } = await recurringActionsService.toggleLineStatus(lineId, currentStatus);

      if (toggleError) {
        setError(toggleError.message || 'שגיאה בשינוי סטטוס');
        return;
      }

      setSuccess('הסטטוס עודכן בהצלחה');
      await loadRecurringActions();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Toggle recurring status error:', err);
      setError('שגיאה בשינוי סטטוס');
    }
  };

  const handleRecurringEdit = (action: RecurringActionLine) => {
    setEditingRecurringLine(action);
    setShowEditRecurringModal(true);
  };

  const handleSaveRecurringEdit = async (updates: Partial<RecurringActionLineInsert>, id: string) => {
    if (!company) return;

    setSavingRecurring(true);
    setError(null);
    setSuccess(null);

    try {
      const { error: updateError } = await recurringActionsService.updateLine(id, updates);

      if (updateError) {
        setError(updateError.message || 'שגיאה בעדכון פעולה');
        return;
      }

      setSuccess('פעולה עודכנה בהצלחה');
      setShowEditRecurringModal(false);
      setEditingRecurringLine(null);
      await loadRecurringActions();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Update recurring action error:', err);
      setError('שגיאה בעדכון פעולה');
    } finally {
      setSavingRecurring(false);
    }
  };

  const handleCloseRecurringEditModal = () => {
    setShowEditRecurringModal(false);
    setEditingRecurringLine(null);
  };

  const handleToggleRecurringRollover = async (lineId: string, currentDisabled: boolean) => {
    setError(null);
    setSuccess(null);
    try {
      const { error: toggleError } = await recurringActionsService.toggleRollover(lineId, currentDisabled);
      if (toggleError) {
        setError(toggleError.message || 'שגיאה בשינוי הגדרת גלגול');
        return;
      }
      setSuccess(currentDisabled ? 'גלגול הופעל' : 'גלגול בוטל');
      await loadRecurringActions();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Toggle recurring rollover error:', err);
      setError('שגיאה בשינוי הגדרת גלגול');
    }
  };

  // Known actions handlers
  const handleSaveKnownAction = async (action: KnownActionInsert, id?: string) => {
    if (!company) return;

    setSavingKnown(true);
    setError(null);
    setSuccess(null);

    try {
      if (id) {
        // Edit mode
        const { error: updateError } = await knownActionsService.updateKnownAction(id, action);

        if (updateError) {
          setError(updateError.message || 'שגיאה בעדכון פעולה ידועה');
          return;
        }

        setSuccess('פעולה ידועה עודכנה בהצלחה');
      } else {
        // Create mode
        const { error: createError } = await knownActionsService.createKnownAction(action);

        if (createError) {
          setError(createError.message || 'שגיאה בשמירת פעולה ידועה');
          return;
        }

        setSuccess('פעולה ידועה נשמרה בהצלחה');
      }

      setShowKnownModal(false);
      setEditingKnownAction(null);
      await loadKnownActions();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Save known action error:', err);
      setError('שגיאה בשמירת פעולה ידועה');
    } finally {
      setSavingKnown(false);
    }
  };

  const handleKnownSort = (field: 'action_date' | 'description' | 'bank_account' | 'accounting_category' | 'amount') => {
    if (knownSortField === field) {
      setKnownSortDirection(knownSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setKnownSortField(field);
      setKnownSortDirection('asc');
    }
  };

  const getKnownSortIcon = (field: 'action_date' | 'description' | 'bank_account' | 'accounting_category' | 'amount') => {
    if (knownSortField !== field) {
      return <ArrowUpDown className="h-4 w-4" />;
    }
    return knownSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  const handleKnownSelectAll = () => {
    if (knownSelectedIds.size === filteredKnownActions.length) {
      setKnownSelectedIds(new Set());
    } else {
      setKnownSelectedIds(new Set(filteredKnownActions.map(a => a.id)));
    }
  };

  const handleKnownSelectOne = (id: string) => {
    const newSelected = new Set(knownSelectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setKnownSelectedIds(newSelected);
  };

  const handleKnownBulkDelete = async () => {
    if (knownSelectedIds.size === 0) return;

    const count = knownSelectedIds.size;
    if (!window.confirm(`האם אתה בטוח שברצונך למחוק ${count} פעולות?`)) {
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      const { error: deleteError } = await knownActionsService.bulkDeleteKnownActions(
        Array.from(knownSelectedIds)
      );

      if (deleteError) {
        setError('שגיאה במחיקת פעולות');
        return;
      }

      setSuccess(`${count} פעולות נמחקו בהצלחה`);
      setKnownSelectedIds(new Set());
      await loadKnownActions();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Bulk delete known error:', err);
      setError('שגיאה במחיקת פעולות');
    }
  };

  const handleKnownDelete = async (id: string) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק פעולה זו?')) {
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      const { error: deleteError } = await knownActionsService.deleteKnownAction(id);

      if (deleteError) {
        setError('שגיאה במחיקת פעולה');
        return;
      }

      setSuccess('פעולה נמחקה בהצלחה');
      await loadKnownActions();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Delete known action error:', err);
      setError('שגיאה במחיקת פעולה');
    }
  };

  const handleToggleKnownStatus = async (actionId: string, currentStatus: string) => {
    setError(null);
    setSuccess(null);

    try {
      const { error: toggleError } = await knownActionsService.toggleActionStatus(actionId, currentStatus);

      if (toggleError) {
        setError(toggleError.message || 'שגיאה בשינוי סטטוס');
        return;
      }

      setSuccess('הסטטוס עודכן בהצלחה');
      await loadKnownActions();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Toggle known status error:', err);
      setError('שגיאה בשינוי סטטוס');
    }
  };

  const handleKnownEdit = (action: KnownAction) => {
    setEditingKnownAction(action);
    setShowKnownModal(true);
  };

  const handleCloseKnownModal = () => {
    setShowKnownModal(false);
    setEditingKnownAction(null);
  };

  const handleToggleKnownRollover = async (actionId: string, currentDisabled: boolean) => {
    setError(null);
    setSuccess(null);
    try {
      const { error: toggleError } = await knownActionsService.toggleRollover(actionId, currentDisabled);
      if (toggleError) {
        setError(toggleError.message || 'שגיאה בשינוי הגדרת גלגול');
        return;
      }
      setSuccess(currentDisabled ? 'גלגול הופעל' : 'גלגול בוטל');
      await loadKnownActions();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Toggle known rollover error:', err);
      setError('שגיאה בשינוי הגדרת גלגול');
    }
  };

  const formatAmount = (amount: number) => {
    const formatted = new Intl.NumberFormat('he-IL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Math.abs(amount));
    
    return amount >= 0 ? `₪${formatted}` : `-₪${formatted}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="h-full">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Table className="h-6 w-6 text-primary-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            {t('cashflow.tables.title')}
          </h1>
        </div>
        <p className="text-gray-600 mt-2">
          {t('cashflow.tables.description')}
        </p>
      </div>

      {/* Tab Navigation — order: Forecast | Recurring | Known | Bank | Reconciliation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8" aria-label="Cashflow tabs">

          {/* Tab 1 — Cashflow Forecast (DEFAULT) */}
          <button
            onClick={() => setActiveTab('cashflow')}
            className={`
              group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
              transition-colors duration-200
              ${activeTab === 'cashflow'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
            `}
            aria-current={activeTab === 'cashflow' ? 'page' : undefined}
          >
            <TrendingUp
              className={`-ml-0.5 mr-2 h-5 w-5 ${activeTab === 'cashflow' ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}`}
              aria-hidden="true"
            />
            תזרים מאוחד
          </button>

          {/* Tab 2 — Recurring Actions */}
          <button
            onClick={() => setActiveTab('recurring')}
            className={`
              group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
              transition-colors duration-200
              ${activeTab === 'recurring'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
            `}
            aria-current={activeTab === 'recurring' ? 'page' : undefined}
          >
            <RefreshCw
              className={`-ml-0.5 mr-2 h-5 w-5 ${activeTab === 'recurring' ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}`}
              aria-hidden="true"
            />
            פעולות קבועות
          </button>

          {/* Tab 3 — Known Actions */}
          <button
            onClick={() => setActiveTab('known')}
            className={`
              group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
              transition-colors duration-200
              ${activeTab === 'known'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
            `}
            aria-current={activeTab === 'known' ? 'page' : undefined}
          >
            <FileText
              className={`-ml-0.5 mr-2 h-5 w-5 ${activeTab === 'known' ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}`}
              aria-hidden="true"
            />
            פעולות ידועות
          </button>

          {/* Tab 4 — Bank Transactions */}
          <button
            onClick={() => setActiveTab('bank')}
            className={`
              group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
              transition-colors duration-200
              ${activeTab === 'bank'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
            `}
            aria-current={activeTab === 'bank' ? 'page' : undefined}
          >
            <Receipt
              className={`-ml-0.5 mr-2 h-5 w-5 ${activeTab === 'bank' ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}`}
              aria-hidden="true"
            />
            טבלת בנק
          </button>

          {/* Tab 5 — Bank Reconciliation */}
          <button
            onClick={() => setActiveTab('reconciliation')}
            className={`
              group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
              transition-colors duration-200
              ${activeTab === 'reconciliation'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
            `}
            aria-current={activeTab === 'reconciliation' ? 'page' : undefined}
          >
            <ArrowLeftRight
              className={`-ml-0.5 mr-2 h-5 w-5 ${activeTab === 'reconciliation' ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}`}
              aria-hidden="true"
            />
            התאמות בנק
          </button>

          {/* Tab 6 — Match History */}
          <button
            onClick={() => setActiveTab('matchHistory')}
            className={`
              group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
              transition-colors duration-200
              ${activeTab === 'matchHistory'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
            `}
            aria-current={activeTab === 'matchHistory' ? 'page' : undefined}
          >
            <EyeOff
              className={`-ml-0.5 mr-2 h-5 w-5 ${activeTab === 'matchHistory' ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}`}
              aria-hidden="true"
            />
            היסטוריית התאמות
          </button>

        </nav>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 rounded-md bg-green-50 border border-green-200 p-4">
          <div className="flex">
            <CheckCircle2 className="h-5 w-5 text-green-400 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800 whitespace-pre-line">{success}</p>
            </div>
            <button
              type="button"
              onClick={() => setSuccess(null)}
              className="ml-auto -mx-1.5 -my-1.5 rounded-md p-1.5 text-green-500 hover:bg-green-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 rounded-md bg-red-50 border border-red-200 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800 whitespace-pre-line">{error}</p>
            </div>
            <button
              type="button"
              onClick={() => setError(null)}
              className="ml-auto -mx-1.5 -my-1.5 rounded-md p-1.5 text-red-500 hover:bg-red-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'bank' && (
        <>
          {/* Transactions Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {/* Toolbar */}
        <div className="mb-6 space-y-4">
          {/* Top Row: Title and Action Buttons */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                {t('cashflow.tables.title')}
              </h2>
            </div>
            <div className="flex items-center gap-3">
              {canEdit && selectedIds.size > 0 && (
                <button
                  onClick={handleBulkDelete}
                  className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <Trash className="h-4 w-4 mr-2" />
                  {t('cashflow.tables.deleteSelectedButton', { count: selectedIds.size })}
                </button>
              )}
              {canEdit && (
                <>
                  {financyConnected && (
                    <>
                      <button
                        onClick={handleFinancyPull}
                        disabled={pullingBank}
                        className="inline-flex items-center px-4 py-2 border border-teal-300 rounded-md shadow-sm text-sm font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {pullingBank ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4 mr-2" />
                        )}
                        {pullingBank ? 'מושך מהבנק…' : 'משיכה מהירה'}
                      </button>
                      <button
                        onClick={() => setShowFinancyModal(true)}
                        disabled={pullingBank}
                        className="inline-flex items-center px-4 py-2 border border-teal-600 rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        משיכה מבוקרת
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setShowImportModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {t('cashflow.tables.uploadButton')}
                  </button>
                  <button
                    onClick={handleAddClick}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t('cashflow.tables.addButton')}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('cashflow.tables.searchPlaceholder')}
              className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
            />
          </div>

          {/* Filter row: period filter + unclassified toggle */}
          <div className="flex flex-wrap items-center gap-3">
            <PeriodFilter
              from={filterDateFrom}
              to={filterDateTo}
              activePreset={bankPreset}
              onChange={(range: DateRange, preset: PeriodPreset | null) => {
                setFilterDateFrom(range.from);
                setFilterDateTo(range.to);
                setBankPreset(preset);
              }}
            />

            {/* Spacer */}
            <div className="flex-1" />

            {/* Unclassified-only toggle */}
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <span className="text-sm font-medium text-gray-700">הצג ללא סיווג בלבד</span>
              <button
                type="button"
                role="switch"
                aria-checked={showUnclassifiedOnly}
                onClick={() => setShowUnclassifiedOnly((v) => !v)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  showUnclassifiedOnly ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    showUnclassifiedOnly ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              {showUnclassifiedOnly && (
                <span className="text-xs font-semibold text-primary-700 bg-primary-50 border border-primary-200 px-2 py-0.5 rounded-full">
                  {filteredTransactions.filter((tx) => !tx.accounting_category_id).length === filteredTransactions.length
                    ? `${filteredTransactions.length} ללא סיווג`
                    : `${filteredTransactions.length} ללא סיווג`}
                </span>
              )}
            </label>
          </div>
        </div>

        {/* Table or Empty State */}
        {filteredTransactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="p-4 bg-gray-100 rounded-full mb-4">
              <Receipt className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? 'No results found' : t('cashflow.tables.emptyState.title')}
            </h3>
            <p className="text-gray-600 text-center max-w-md">
              {searchQuery 
                ? 'Try adjusting your search query'
                : t('cashflow.tables.emptyState.description')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {canEdit && (
                    <th className="px-6 py-3 text-right">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === filteredTransactions.length && filteredTransactions.length > 0}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                    </th>
                  )}
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    סטטוס
                  </th>
                  <th 
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('transaction_date')}
                  >
                    <div className="flex items-center justify-end gap-2">
                      {t('cashflow.tables.table.date')}
                      {getSortIcon('transaction_date')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('description')}
                  >
                    <div className="flex items-center justify-end gap-2">
                      {t('cashflow.tables.table.description')}
                      {getSortIcon('description')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('bank_account')}
                  >
                    <div className="flex items-center justify-end gap-2">
                      {t('cashflow.tables.table.bankAccount')}
                      {getSortIcon('bank_account')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('classification')}
                  >
                    <div className="flex items-center justify-end gap-2">
                      סיווג הנהלת חשבונות
                      {getSortIcon('classification')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    סיווג תזרים מזומנים
                  </th>
                  <th 
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('amount')}
                  >
                    <div className="flex items-center justify-end gap-2">
                      {t('cashflow.tables.table.amount')}
                      {getSortIcon('amount')}
                    </div>
                  </th>
                  {canEdit && (
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1 whitespace-nowrap">
                      {t('cashflow.tables.table.actions')}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTransactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    {canEdit && (
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(transaction.id)}
                          onChange={() => handleSelectOne(transaction.id)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                      </td>
                    )}
                    <td className="px-3 py-4 whitespace-nowrap text-center">
                      <StatusIcon status={transaction.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm text-gray-900">{formatDate(transaction.transaction_date)}</div>
                    </td>
                    {/* ── Inline-editable description cell ─────────────────── */}
                    <td className="px-6 py-4 text-right max-w-xs">
                      {editingDescId === transaction.id ? (
                        /* ── Edit mode ─── */
                        <input
                          type="text"
                          autoFocus
                          dir="rtl"
                          readOnly={!canEdit}
                          disabled={savingDescIds.has(transaction.id)}
                          value={editingDescValue}
                          onChange={(e) => setEditingDescValue(e.target.value)}
                          onBlur={() => handleDescSave(transaction.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleDescSave(transaction.id);
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              handleDescCancel();
                            }
                          }}
                          className={`w-full text-sm text-gray-900 border rounded px-2 py-1
                                      focus:outline-none focus:ring-2 focus:ring-primary-500
                                      bg-white shadow-sm
                                      ${canEdit ? 'border-primary-400' : 'border-gray-200 bg-gray-50 cursor-default'}`}
                        />
                      ) : (
                        /* ── Display mode ─── */
                        <div className="flex items-center gap-1.5 min-w-0">
                          {savingDescIds.has(transaction.id) && (
                            <Loader2 className="h-3.5 w-3.5 text-primary-500 animate-spin flex-shrink-0" />
                          )}
                          <span
                            title="לחץ לעריכת התיאור"
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log('[CashflowTables] span clicked | id:', transaction.id, '| canEdit:', canEdit, '| isSuperAdmin:', isSuperAdmin, '| canEditCashflow:', canEditCashflow);
                              if (!savingDescIds.has(transaction.id)) {
                                handleDescClick(transaction);
                              }
                            }}
                            className={`text-sm text-gray-900 truncate block cursor-text
                              select-none
                              underline decoration-dashed decoration-gray-300
                              underline-offset-2
                              hover:decoration-primary-400 hover:text-primary-700
                              transition-colors
                              ${savingDescIds.has(transaction.id) ? 'opacity-50 cursor-wait' : ''}`}
                          >
                            {transaction.description || <span className="text-gray-400 italic">—</span>}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm text-gray-500">
                        {transaction.bank_accounts?.nickname || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {/* Inline category selector — optimistic update, no scroll reset */}
                      <div className="flex items-center justify-end gap-1.5">
                        {savingCategoryIds.has(transaction.id) && (
                          <Loader2 className="h-3.5 w-3.5 text-primary-500 animate-spin flex-shrink-0" />
                        )}
                        <select
                          value={transaction.accounting_category_id ?? ''}
                          onChange={(e) => handleInlineCategoryChange(transaction.id, e.target.value)}
                          disabled={savingCategoryIds.has(transaction.id) || transaction.status === 'matched'}
                          title={transaction.status === 'matched' ? 'לא ניתן לשנות סיווג של פעולה מותאמת' : undefined}
                          className={`text-sm border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 max-w-[200px] ${
                            !transaction.accounting_category_id
                              ? 'border-amber-400 bg-amber-50 text-amber-700 font-medium'
                              : 'border-gray-200 bg-white text-gray-900'
                          } ${savingCategoryIds.has(transaction.id) ? 'opacity-60' : ''}`}
                          dir="rtl"
                        >
                          {/* Placeholder shown only when no category is set — not a real selectable value */}
                          {!transaction.accounting_category_id && (
                            <option value="" disabled>⚠ טרם סווג</option>
                          )}
                          {/* Empty option that lets the user explicitly remove a category */}
                          <option value="">— הסר סיווג —</option>
                          {accountingCategories.map((cat) => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))}
                        </select>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {transaction.accounting_categories?.cashflow_mapping 
                          ? getCashflowMappingLabel(transaction.accounting_categories.cashflow_mapping)
                          : '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className={`text-sm font-medium ${
                        transaction.is_cc_settlement ? 'text-gray-400 line-through' :
                        transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatAmount(transaction.amount)}
                      </div>
                      {transaction.is_cc_settlement && (
                        <span className="text-[10px] text-gray-400">חיוב כרטיס</span>
                      )}
                    </td>
                    {canEdit && (
                      <td className="px-2 py-4 whitespace-nowrap text-center w-1">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={async () => {
                              const newVal = !transaction.is_cc_settlement;
                              setTransactions(prev => prev.map(tx => tx.id === transaction.id ? { ...tx, is_cc_settlement: newVal } : tx));
                              const { error: updErr } = await bankTransactionsService.updateBankTransaction(transaction.id, { is_cc_settlement: newVal });
                              if (updErr) {
                                setTransactions(prev => prev.map(tx => tx.id === transaction.id ? { ...tx, is_cc_settlement: !newVal } : tx));
                                emitToast('שגיאה בעדכון סימון כרטיס אשראי', 'error');
                              }
                            }}
                            title={transaction.is_cc_settlement ? 'בטל סימון חיוב כרטיס' : 'סמן כחיוב כרטיס (לא ייספר בתזרים)'}
                            className={`p-1.5 rounded border text-xs focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-500 ${
                              transaction.is_cc_settlement
                                ? 'border-blue-300 bg-blue-50 text-blue-600 hover:bg-blue-100'
                                : 'border-gray-300 text-gray-600 bg-white hover:bg-gray-50'
                            }`}
                          >
                            CC
                          </button>
                          <button
                            onClick={() => handleEditClick(transaction)}
                            title={t('cashflow.tables.editButton')}
                            className="p-1.5 rounded border border-gray-300 text-gray-600 bg-white hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-500"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleToggleBankStatus(transaction.id, transaction.status || 'pending')}
                            disabled={transaction.status === 'matched'}
                            title={transaction.status === 'matched' ? 'לא ניתן לשנות סטטוס של פעולה מותאמת' : (transaction.status === 'ignored' ? 'סמן כממתין' : 'סמן כמתעלם')}
                            className={`p-1.5 rounded border focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                              transaction.status === 'matched'
                                ? 'border-gray-200 text-gray-300 bg-gray-50 cursor-not-allowed'
                                : 'border-gray-300 text-gray-600 bg-white hover:bg-gray-50 hover:text-gray-900 focus:ring-primary-500'
                            }`}
                          >
                            <EyeOff className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleDelete(transaction.id)}
                            title={t('cashflow.tables.deleteButton')}
                            className="p-1.5 rounded border border-red-300 text-red-600 bg-white hover:bg-red-50 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-500"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Transaction Modal - keeping the same as before */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingTransaction
                    ? t('cashflow.tables.form.editTitle')
                    : t('cashflow.tables.form.title')}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6">
              {/* Transaction Date */}
              <div>
                <label htmlFor="transaction_date" className="block text-sm font-medium text-gray-700 mb-1 text-right">
                  {t('cashflow.tables.form.date')}
                </label>
                <input
                  type="date"
                  id="transaction_date"
                  value={formData.transaction_date}
                  onChange={(e) => setFormData({ ...formData, transaction_date: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1 text-right">
                  {t('cashflow.tables.form.description')}
                </label>
                <input
                  type="text"
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('cashflow.tables.form.descriptionPlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
                  required
                />
              </div>

              {/* Bank Account */}
              <div>
                <label htmlFor="bank_account_id" className="block text-sm font-medium text-gray-700 mb-1 text-right">
                  {t('cashflow.tables.form.bankAccount')}
                </label>
                <select
                  id="bank_account_id"
                  value={formData.bank_account_id}
                  onChange={(e) => setFormData({ ...formData, bank_account_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
                  required
                >
                  <option value="">{t('cashflow.tables.form.bankAccountPlaceholder')}</option>
                  {bankAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.nickname}
                    </option>
                  ))}
                </select>
              </div>

              {/* Accounting Category */}
              <div>
                <label htmlFor="accounting_category" className="block text-sm font-medium text-gray-700 mb-1 text-right">
                  סיווג הנהלת חשבונות
                </label>
                <select
                  id="accounting_category"
                  value={formData.accounting_category_id}
                  onChange={(e) => setFormData({ ...formData, accounting_category_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
                  required
                >
                  <option value="">בחר סיווג</option>
                  {accountingCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name} ({getCashflowMappingLabel(category.cashflow_mapping)})
                    </option>
                  ))}
                </select>
                {accountingCategories.length === 0 && (
                  <p className="mt-1 text-xs text-yellow-600 text-right">
                    אין סיווגים חשבונאיים. אנא הגדר סיווגים בהגדרות תזרים.
                  </p>
                )}
              </div>

              {/* Amount */}
              <div>
                <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1 text-right">
                  {t('cashflow.tables.form.amount')}
                </label>
                <input
                  type="number"
                  step="0.01"
                  id="amount"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder={t('cashflow.tables.form.amountPlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
                  required
                />
                <p className="mt-1 text-xs text-gray-500 text-right">
                  {t('cashflow.tables.form.amountHint')}
                </p>
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {t('cashflow.tables.form.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4 mr-2" />
                      {t('cashflow.tables.form.submitting')}
                    </>
                  ) : (
                    t('cashflow.tables.form.submit')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Transactions Modal */}
      <ImportTransactionsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={handleImportSuccess}
        bankAccounts={bankAccounts}
        accountingCategories={accountingCategories}
        companyId={activeCompanyId}
      />
      {activeCompanyId && (
        <>
          <FinancyPullModal
            isOpen={showFinancyModal}
            onClose={() => setShowFinancyModal(false)}
            companyId={activeCompanyId}
            onSuccess={(imported, newTransactionIds) => {
              setShowFinancyModal(false);
              if (newTransactionIds && newTransactionIds.length > 0) {
                setReviewTxnIds(newTransactionIds);
                setShowReviewModal(true);
                loadData();
              } else {
                setSuccess(
                  imported > 0
                    ? `נמשכו ${imported} תנועות חדשות מהבנק לטבלת הבנק.`
                    : 'לא נמצאו תנועות חדשות למשיכה.',
                );
                loadData();
              }
            }}
          />
          <ImportReviewModal
            isOpen={showReviewModal}
            onClose={() => setShowReviewModal(false)}
            companyId={activeCompanyId}
            transactionIds={reviewTxnIds}
            onComplete={() => {
              loadData();
              setSuccess('הסיווגים עודכנו בהצלחה.');
              setTimeout(() => setSuccess(null), 4000);
            }}
          />
        </>
      )}
        </>
      )}

      {/* Recurring Transactions Tab */}
      {activeTab === 'recurring' && (
        <>
          {/* Recurring Actions Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {/* Toolbar */}
            <div className="mb-6 space-y-4">
              {/* Top Row: Title and Action Buttons */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                    <RefreshCw className="h-5 w-5" />
                    טבלת פעולות קבועות
                  </h2>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={exportRecurringToExcel}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    title="ייצא לאקסל"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    ייצא לאקסל
                  </button>
                  {canEdit && (
                    <button
                      onClick={() => setShowRecurringModal(true)}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      הוסף פעולה קבועה
                    </button>
                  )}
                  {canEdit && (
                    <button
                      onClick={() => setShowDiscoveryWizard(true)}
                      className="inline-flex items-center px-4 py-2 border border-primary-300 rounded-md shadow-sm text-sm font-medium text-primary-700 bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      גלה פעולות קבועות מאקסל
                    </button>
                  )}
                </div>
              </div>

              {/* Bulk Actions Toolbar — visible only when rows are selected */}
              {canEdit && recurringSelectedIds.size > 0 && (
                <div className="flex items-center gap-3 px-4 py-3 bg-primary-50 border border-primary-200 rounded-lg">
                  {/* Selection count badge */}
                  <span className="text-sm font-semibold text-primary-700 min-w-max">
                    {recurringSelectedIds.size} נבחרו
                  </span>

                  <div className="h-5 w-px bg-primary-300" />

                  {/* ── Cancel Rollover ── */}
                  <button
                    onClick={handleRecurringBulkCancelRollover}
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-md border border-orange-300 text-sm font-medium text-orange-700 bg-white hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-orange-400 transition-colors"
                    title="בטל גלגול אוטומטי לפעולות הנבחרות"
                  >
                    <RefreshCw className="h-4 w-4" />
                    ביטול גלגול
                  </button>

                  {/* ── Bulk Edit ── */}
                  <button
                    onClick={() => setBulkEditOpen(true)}
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-md border border-primary-300 text-sm font-medium text-primary-700 bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-400 transition-colors"
                    title="ערוך שדות משותפים לפעולות הנבחרות"
                  >
                    <Edit2 className="h-4 w-4" />
                    ✏️ ערוך נבחרים
                  </button>

                  {/* ── Bulk Delete ── */}
                  <button
                    onClick={handleRecurringBulkDelete}
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-md border border-red-300 text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-400 transition-colors"
                    title="מחק את הפעולות הנבחרות"
                  >
                    <Trash className="h-4 w-4" />
                    מחק נבחרים
                  </button>

                  {/* ── Clear selection ── */}
                  <button
                    onClick={() => setRecurringSelectedIds(new Set())}
                    className="mr-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none transition-colors"
                    title="בטל בחירה"
                  >
                    <X className="h-4 w-4" />
                    בטל בחירה
                  </button>
                </div>
              )}

              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={recurringSearchQuery}
                  onChange={(e) => setRecurringSearchQuery(e.target.value)}
                  placeholder="חיפוש לפי תאריך, תיאור, חשבון או סכום..."
                  className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
                />
              </div>
            </div>

            {/* Period filter */}
            <div className="mt-3">
              <PeriodFilter
                from={recurringDateFrom}
                to={recurringDateTo}
                activePreset={recurringPreset}
                onChange={(range: DateRange, preset: PeriodPreset | null) => {
                  setRecurringDateFrom(range.from);
                  setRecurringDateTo(range.to);
                  setRecurringPreset(preset);
                }}
              />
            </div>

            {/* Table or Empty State */}
            {recurringLoading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
              </div>
            ) : filteredRecurringActions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="p-4 bg-gray-100 rounded-full mb-4">
                  <RefreshCw className="h-12 w-12 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {recurringSearchQuery ? 'לא נמצאו תוצאות' : 'אין פעולות קבועות'}
                </h3>
                <p className="text-gray-600 text-center max-w-md">
                  {recurringSearchQuery 
                    ? 'נסה לשנות את מילות החיפוש'
                    : 'התחל בהוספת פעולות קבועות באמצעות הכפתור למעלה'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {canEdit && (
                        <th className="px-6 py-3 text-right">
                          <input
                            type="checkbox"
                            checked={recurringSelectedIds.size === filteredRecurringActions.length && filteredRecurringActions.length > 0}
                            onChange={handleRecurringSelectAll}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                        </th>
                      )}
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        סטטוס
                      </th>
                      <th 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleRecurringSort('action_date')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          תאריך
                          {getRecurringSortIcon('action_date')}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleRecurringSort('description')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          תיאור
                          {getRecurringSortIcon('description')}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleRecurringSort('bank_account')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          חשבון בנק
                          {getRecurringSortIcon('bank_account')}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleRecurringSort('accounting_category')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          סיווג חשבונאי
                          {getRecurringSortIcon('accounting_category')}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleRecurringSort('amount')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          סכום
                          {getRecurringSortIcon('amount')}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        חוק קיזוז
                      </th>
                      {canEdit && (
                        <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1 whitespace-nowrap">
                          פעולות
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredRecurringActions.map((action) => (
                      <tr key={action.id} className="hover:bg-gray-50">
                        {canEdit && (
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <input
                              type="checkbox"
                              checked={recurringSelectedIds.has(action.id)}
                              onChange={() => handleRecurringSelectOne(action.id)}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                          </td>
                        )}
                        <td className="px-3 py-4 whitespace-nowrap text-center">
                          <StatusIcon status={action.status} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          {/* Date cell — show rolling / snoozed indicator */}
                          {(() => {
                            const rolling      = isRolling(action);
                            const snoozed      = isSnoozed(action);
                            const effectiveDate = getEffectiveDate(action);
                            const label        = getRolloverLabel(action);
                            return (
                              <div className="flex flex-col items-end gap-0.5">
                                <div className={`text-sm ${
                                  snoozed   ? 'text-blue-600 font-medium' :
                                  rolling   ? 'text-red-600  font-medium' : 'text-gray-900'
                                }`}>
                                  {rolling || snoozed ? (
                                    <span className="flex items-center gap-1">
                                      {snoozed
                                        ? <AlarmClock className="w-3 h-3" />
                                        : <RefreshCw  className="w-3 h-3 animate-spin" />
                                      }
                                      {formatDate(effectiveDate)}
                                    </span>
                                  ) : (
                                    formatDate(action.action_date)
                                  )}
                                </div>
                                {/* Strikethrough original date when rolling/snoozed */}
                                {(rolling || snoozed) && (
                                  <div className="text-[10px] text-gray-400 line-through">
                                    {formatDate(action.action_date)}
                                  </div>
                                )}
                                {/* Label (e.g. "גלגול מושבת") when not actively rolling */}
                                {label && !rolling && !snoozed && (
                                  <span className="text-[10px] text-gray-400">{label}</span>
                                )}
                                {/* Snooze badge */}
                                {snoozed && (
                                  <span className="inline-flex items-center gap-0.5 text-[10px] text-blue-600 bg-blue-50 border border-blue-200 rounded-full px-1.5 py-0.5 font-medium">
                                    <AlarmClock className="w-2.5 h-2.5" />
                                    נדחה
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="text-sm text-gray-900">{action.description}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="text-sm text-gray-500">
                            {action.bank_accounts?.nickname || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="text-sm text-gray-900">
                            {action.accounting_categories?.name || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          {/* ── Offset-aware amount cell ──────────────────────────────────────
                              Shows the NET amount after applying the offset rule.
                              When an offset reduces the budget, also shows an info badge:
                              "קוזזו ₪X מתוך ₪Y"  (₪X offset out of ₪Y budget).
                          ─────────────────────────────────────────────────────────────────── */}
                          {(() => {
                            const result: OffsetResult | undefined = offsetMap.get(action.id);
                            const display  = result ? result.displayAmount : action.amount;
                            const raw      = result ? result.rawAmount     : action.amount;
                            const offset   = result ? result.offsetAmount  : 0;
                            const isOffset = result?.isOffset ?? false;

                            return (
                              <div className="flex flex-col items-end gap-0.5">
                                {/* Net (display) amount */}
                                <div className={`text-sm font-medium ${
                                  display >= 0 ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {formatAmount(display)}
                                </div>

                                {/* Offset annotation — visible only when offset > 0 */}
                                {isOffset && (
                                  <div
                                    className="flex items-center gap-1 text-[11px] text-amber-600 font-medium"
                                    title={`תקציב מקורי: ${formatAmount(raw)} | קוזז: ${formatAmount(offset)}`}
                                  >
                                    <Info className="w-3 h-3 flex-shrink-0" />
                                    <span>קוזזו {formatAmount(offset)} מתוך {formatAmount(raw)}</span>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="text-sm text-gray-900">
                            {action.offset_codes?.name || 'ללא'}
                          </div>
                        </td>
                        {canEdit && (
                          <td className="px-2 py-4 whitespace-nowrap text-center w-1">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => handleRecurringEdit(action)}
                                title="עריכה"
                                className="p-1.5 rounded border border-gray-300 text-gray-600 bg-white hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-500"
                              >
                                <Edit2 className="h-3.5 w-3.5" />
                              </button>

                              {/* ── Snooze / Rollover management button ── */}
                              {action.status !== 'matched' && (
                                <div className="relative">
                                  <button
                                    onClick={() => setSnoozeTarget(
                                      snoozeTarget?.id === action.id
                                        ? null
                                        : {
                                            id: action.id,
                                            sourceTable: 'recurring',
                                            currentSnooze: action.snoozed_until ?? null,
                                            originalDate: action.action_date,
                                          }
                                    )}
                                    title={
                                      isSnoozed(action)
                                        ? `נדחה עד ${action.snoozed_until} – לחץ לשינוי`
                                        : isRolling(action)
                                          ? 'פעולה מגולגלת – לחץ לדחייה או ביטול'
                                          : 'ניהול גלגול'
                                    }
                                    className={`p-1.5 rounded border focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                                      isSnoozed(action)
                                        ? 'border-blue-300 text-blue-600 bg-blue-50 hover:bg-blue-100 focus:ring-blue-400'
                                        : action.disable_rollover
                                          ? 'border-green-300 text-green-700 bg-white hover:bg-green-50 focus:ring-green-500'
                                          : isRolling(action)
                                            ? 'border-orange-300 text-orange-700 bg-white hover:bg-orange-50 focus:ring-orange-400'
                                            : 'border-gray-300 text-gray-500 bg-white hover:bg-gray-50 focus:ring-gray-400'
                                    }`}
                                  >
                                    {isSnoozed(action)
                                      ? <AlarmClock className="h-3.5 w-3.5" />
                                      : <RefreshCw  className={`h-3.5 w-3.5 ${isRolling(action) && !action.disable_rollover ? 'animate-spin' : ''}`} />
                                    }
                                  </button>

                                  {/* Popover */}
                                  {snoozeTarget?.id === action.id && snoozeTarget.sourceTable === 'recurring' && (
                                    <RolloverSnoozePopover
                                      actionId={action.id}
                                      sourceTable="recurring"
                                      currentSnooze={action.snoozed_until ?? null}
                                      originalDate={action.action_date}
                                      onClose={() => setSnoozeTarget(null)}
                                      onUpdated={() => { setSnoozeTarget(null); loadRecurringActions(); }}
                                    />
                                  )}
                                </div>
                              )}

                              {/* Restore-rollover button when disable_rollover=true */}
                              {action.status !== 'matched' && action.disable_rollover && (
                                <button
                                  onClick={() => handleToggleRecurringRollover(action.id, true)}
                                  title="הפעל גלגול אוטומטי"
                                  className="p-1.5 rounded border border-green-300 text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-500"
                                >
                                  <RefreshCw className="h-3.5 w-3.5" />
                                </button>
                              )}

                              <button
                                onClick={() => handleToggleRecurringStatus(action.id, action.status || 'pending')}
                                disabled={action.status === 'matched'}
                                title={action.status === 'matched' ? 'לא ניתן לשנות סטטוס של פעולה מותאמת' : (action.status === 'ignored' ? 'סמן כממתין' : 'סמן כמתעלם')}
                                className={`p-1.5 rounded border focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                                  action.status === 'matched'
                                    ? 'border-gray-200 text-gray-300 bg-gray-50 cursor-not-allowed'
                                    : 'border-gray-300 text-gray-600 bg-white hover:bg-gray-50 hover:text-gray-900 focus:ring-primary-500'
                                }`}
                              >
                                <EyeOff className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => handleRecurringDelete(action.id)}
                                title="מחיקה"
                                className="p-1.5 rounded border border-red-300 text-red-600 bg-white hover:bg-red-50 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-500"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Known Actions Tab */}
      {activeTab === 'known' && (
        <>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Header with Title and Add Button */}
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <FileText className="h-6 w-6" />
                  טבלת פעולות ידועות
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  נהל פעולות ידועות וצפויות שלא חוזרות על עצמן
                </p>
              </div>
              {canEdit ? (
                <div className="flex items-center gap-3">
                  <button
                    onClick={exportKnownToExcel}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    title="ייצא לאקסל"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    ייצא לאקסל
                  </button>
                  <button
                    onClick={() => setShowImportKnownModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    ייבא מאקסל
                  </button>
                  <button
                    onClick={() => setShowKnownModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    הוסף פעולה ידועה
                  </button>
                </div>
              ) : (
                <button
                  onClick={exportKnownToExcel}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  title="ייצא לאקסל"
                >
                  <Download className="h-4 w-4 mr-2" />
                  ייצא לאקסל
                </button>
              )}
            </div>

            {/* Search Bar and Bulk Actions */}
            <div className="p-6 border-b">
              <div className="flex items-center justify-between gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="חיפוש לפי תאריך, תיאור, חשבון, סיווג או סכום..."
                    value={knownSearchQuery}
                    onChange={(e) => setKnownSearchQuery(e.target.value)}
                    className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
                  />
                </div>
                {canEdit && knownSelectedIds.size > 0 && (
                  <button
                    onClick={handleKnownBulkDelete}
                    className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <Trash className="h-4 w-4 mr-2" />
                    מחק ({knownSelectedIds.size})
                  </button>
                )}
              </div>
            </div>

            {/* Period filter */}
            <div className="mt-3">
              <PeriodFilter
                from={knownDateFrom}
                to={knownDateTo}
                activePreset={knownPreset}
                onChange={(range: DateRange, preset: PeriodPreset | null) => {
                  setKnownDateFrom(range.from);
                  setKnownDateTo(range.to);
                  setKnownPreset(preset);
                }}
              />
            </div>

            {/* Loading State */}
            {knownLoading && (
              <div className="flex justify-center items-center p-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
              </div>
            )}

            {/* Empty State */}
            {!knownLoading && knownActions.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="p-4 bg-gray-100 rounded-full mb-4">
                  <FileText className="h-12 w-12 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  אין פעולות ידועות
                </h3>
                <p className="text-gray-600 text-center max-w-md">
                  הוסף פעולה ידועה ראשונה כדי להתחיל
                </p>
              </div>
            )}

            {/* Table */}
            {!knownLoading && knownActions.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {canEdit && (
                        <th className="px-6 py-3 text-right">
                          <input
                            type="checkbox"
                            checked={knownSelectedIds.size === filteredKnownActions.length && filteredKnownActions.length > 0}
                            onChange={handleKnownSelectAll}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                        </th>
                      )}
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        סטטוס
                      </th>
                      <th 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleKnownSort('action_date')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          תאריך
                          {getKnownSortIcon('action_date')}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleKnownSort('description')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          תיאור
                          {getKnownSortIcon('description')}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleKnownSort('bank_account')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          חשבון בנק
                          {getKnownSortIcon('bank_account')}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleKnownSort('accounting_category')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          סיווג חשבונאי
                          {getKnownSortIcon('accounting_category')}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleKnownSort('amount')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          סכום
                          {getKnownSortIcon('amount')}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        חוק קיזוז
                      </th>
                      {canEdit && (
                        <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1 whitespace-nowrap">
                          פעולות
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredKnownActions.map((action) => (
                      <tr key={action.id} className="hover:bg-gray-50">
                        {canEdit && (
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <input
                              type="checkbox"
                              checked={knownSelectedIds.has(action.id)}
                              onChange={() => handleKnownSelectOne(action.id)}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                          </td>
                        )}
                        <td className="px-3 py-4 whitespace-nowrap text-center">
                          <StatusIcon status={action.status} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          {/* Date cell — show rolling / snoozed indicator */}
                          {(() => {
                            const rolling      = isRolling(action);
                            const snoozed      = isSnoozed(action);
                            const effectiveDate = getEffectiveDate(action);
                            const label        = getRolloverLabel(action);
                            return (
                              <div className="flex flex-col items-end gap-0.5">
                                <div className={`text-sm ${
                                  snoozed   ? 'text-blue-600 font-medium' :
                                  rolling   ? 'text-red-600  font-medium' : 'text-gray-900'
                                }`}>
                                  {rolling || snoozed ? (
                                    <span className="flex items-center gap-1">
                                      {snoozed
                                        ? <AlarmClock className="w-3 h-3" />
                                        : <RefreshCw  className="w-3 h-3 animate-spin" />
                                      }
                                      {formatDate(effectiveDate)}
                                    </span>
                                  ) : (
                                    formatDate(action.action_date)
                                  )}
                                </div>
                                {(rolling || snoozed) && (
                                  <div className="text-[10px] text-gray-400 line-through">
                                    {formatDate(action.action_date)}
                                  </div>
                                )}
                                {label && !rolling && !snoozed && (
                                  <span className="text-[10px] text-gray-400">{label}</span>
                                )}
                                {snoozed && (
                                  <span className="inline-flex items-center gap-0.5 text-[10px] text-blue-600 bg-blue-50 border border-blue-200 rounded-full px-1.5 py-0.5 font-medium">
                                    <AlarmClock className="w-2.5 h-2.5" />
                                    נדחה
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="text-sm text-gray-900">{action.description}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="text-sm text-gray-500">
                            {action.bank_accounts?.nickname || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="text-sm text-gray-900">
                            {action.accounting_categories?.name || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          {/* Known action amount + optional "מקזז" badge when this
                              action is actively used to offset a recurring budget */}
                          <div className="flex flex-col items-end gap-0.5">
                            <div className={`text-sm font-medium ${
                              action.amount >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {formatAmount(action.amount)}
                            </div>
                            {knownOffsetIds.has(action.id) && (
                              <span
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700"
                                title="פעולה זו מקזזת תקציב של פעולה קבועה"
                              >
                                <Info className="w-2.5 h-2.5" />
                                מקזז
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="text-sm text-gray-900">
                            {action.offset_codes?.name || 'ללא'}
                          </div>
                        </td>
                        {canEdit && (
                          <td className="px-2 py-4 whitespace-nowrap text-center w-1">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => handleKnownEdit(action)}
                                title="עריכה"
                                className="p-1.5 rounded border border-gray-300 text-gray-600 bg-white hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-500"
                              >
                                <Edit2 className="h-3.5 w-3.5" />
                              </button>

                              {/* ── Snooze / Rollover management button ── */}
                              {action.status !== 'matched' && (
                                <div className="relative">
                                  <button
                                    onClick={() => setSnoozeTarget(
                                      snoozeTarget?.id === action.id
                                        ? null
                                        : {
                                            id: action.id,
                                            sourceTable: 'known',
                                            currentSnooze: action.snoozed_until ?? null,
                                            originalDate: action.action_date,
                                          }
                                    )}
                                    title={
                                      isSnoozed(action)
                                        ? `נדחה עד ${action.snoozed_until} – לחץ לשינוי`
                                        : isRolling(action)
                                          ? 'פעולה מגולגלת – לחץ לדחייה או ביטול'
                                          : 'ניהול גלגול'
                                    }
                                    className={`p-1.5 rounded border focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                                      isSnoozed(action)
                                        ? 'border-blue-300 text-blue-600 bg-blue-50 hover:bg-blue-100 focus:ring-blue-400'
                                        : action.disable_rollover
                                          ? 'border-green-300 text-green-700 bg-white hover:bg-green-50 focus:ring-green-500'
                                          : isRolling(action)
                                            ? 'border-orange-300 text-orange-700 bg-white hover:bg-orange-50 focus:ring-orange-400'
                                            : 'border-gray-300 text-gray-500 bg-white hover:bg-gray-50 focus:ring-gray-400'
                                    }`}
                                  >
                                    {isSnoozed(action)
                                      ? <AlarmClock className="h-3.5 w-3.5" />
                                      : <RefreshCw  className={`h-3.5 w-3.5 ${isRolling(action) && !action.disable_rollover ? 'animate-spin' : ''}`} />
                                    }
                                  </button>

                                  {/* Popover */}
                                  {snoozeTarget?.id === action.id && snoozeTarget.sourceTable === 'known' && (
                                    <RolloverSnoozePopover
                                      actionId={action.id}
                                      sourceTable="known"
                                      currentSnooze={action.snoozed_until ?? null}
                                      originalDate={action.action_date}
                                      onClose={() => setSnoozeTarget(null)}
                                      onUpdated={() => { setSnoozeTarget(null); loadKnownActions(); }}
                                    />
                                  )}
                                </div>
                              )}

                              {/* Restore-rollover button when disable_rollover=true */}
                              {action.status !== 'matched' && action.disable_rollover && (
                                <button
                                  onClick={() => handleToggleKnownRollover(action.id, true)}
                                  title="הפעל גלגול אוטומטי"
                                  className="p-1.5 rounded border border-green-300 text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-500"
                                >
                                  <RefreshCw className="h-3.5 w-3.5" />
                                </button>
                              )}

                              <button
                                onClick={() => handleToggleKnownStatus(action.id, action.status || 'pending')}
                                disabled={action.status === 'matched'}
                                title={action.status === 'matched' ? 'לא ניתן לשנות סטטוס של פעולה מותאמת' : (action.status === 'ignored' ? 'סמן כממתין' : 'סמן כמתעלם')}
                                className={`p-1.5 rounded border focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                                  action.status === 'matched'
                                    ? 'border-gray-200 text-gray-300 bg-gray-50 cursor-not-allowed'
                                    : 'border-gray-300 text-gray-600 bg-white hover:bg-gray-50 hover:text-gray-900 focus:ring-primary-500'
                                }`}
                              >
                                <EyeOff className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => handleKnownDelete(action.id)}
                                title="מחיקה"
                                className="p-1.5 rounded border border-red-300 text-red-600 bg-white hover:bg-red-50 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-500"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Bank Reconciliation Tab */}
      {activeTab === 'reconciliation' && company && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ArrowLeftRight className="h-6 w-6" />
              התאמות בנק
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              התאם בין תנועות בנק בפועל לבין פעולות צפויות
            </p>
          </div>
          <BankReconciliationView companyId={activeCompanyId} />
        </div>
      )}

      {/* Match History Tab */}
      {activeTab === 'matchHistory' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <EyeOff className="h-6 w-6 text-primary-600" />
              היסטוריית התאמות
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              כל ההתאמות שבוצעו — בנק מול פעולות צפויות. ניתן לבטל כל התאמה ולהחזיר את הפעולות לתור.
            </p>
          </div>
          <MatchHistoryView companyId={activeCompanyId} />
        </div>
      )}

      {/* Cashflow Forecast Tab */}
      {activeTab === 'cashflow' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-primary-600" />
              טבלת תזרים סופית
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              תצוגה מאוחדת של כל התנועות: בנק, פעולות ידועות ופעולות קבועות — כולל יתרה מצטברת
            </p>
          </div>
          <div className="mb-4">
            <PeriodFilter
              from={cashflowDateFrom}
              to={cashflowDateTo}
              activePreset={cashflowPreset}
              onChange={(range: DateRange, preset: PeriodPreset | null) => {
                setCashflowDateFrom(range.from);
                setCashflowDateTo(range.to);
                setCashflowPreset(preset);
              }}
            />
          </div>
          <CashflowForecastTable startDate={cashflowDateFrom} endDate={cashflowDateTo} />
        </div>
      )}

      {/* Modals - Rendered outside tab conditionals so they work from any tab */}
      <AddRecurringActionModal
        isOpen={showRecurringModal}
        onClose={() => { setShowRecurringModal(false); setRecurringPrefill(null); }}
        onSave={handleSaveRecurringAction}
        bankAccounts={bankAccounts}
        accountingCategories={accountingCategories}
        isSaving={savingRecurring}
        companyId={activeCompanyId}
        prefillData={recurringPrefill}
      />

      <RecurringDiscoveryWizard
        isOpen={showDiscoveryWizard}
        onClose={() => setShowDiscoveryWizard(false)}
        existingRecurringActions={recurringActions}
        onApprove={(prefill) => {
          setRecurringPrefill(prefill);
          setShowDiscoveryWizard(false);
          setShowRecurringModal(true);
        }}
      />

      <EditRecurringActionModal
        isOpen={showEditRecurringModal}
        onClose={handleCloseRecurringEditModal}
        onSave={handleSaveRecurringEdit}
        bankAccounts={bankAccounts}
        accountingCategories={accountingCategories}
        isSaving={savingRecurring}
        companyId={activeCompanyId}
        editingLine={editingRecurringLine}
      />

      {/* ── Bulk Edit Recurring Modal ── */}
      <BulkEditRecurringModal
        isOpen={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        selectedRows={recurringActions.filter(r => recurringSelectedIds.has(r.id))}
        accountingCategories={accountingCategories}
        isSaving={bulkEditSaving}
        onSave={handleRecurringBulkEdit}
      />

      <AddKnownActionModal
        isOpen={showKnownModal}
        onClose={handleCloseKnownModal}
        onSave={handleSaveKnownAction}
        bankAccounts={bankAccounts}
        accountingCategories={accountingCategories}
        isSaving={savingKnown}
        companyId={activeCompanyId}
        editingAction={editingKnownAction}
      />

      <ImportKnownActionsModal
        isOpen={showImportKnownModal}
        onClose={() => setShowImportKnownModal(false)}
        onSuccess={() => { setShowImportKnownModal(false); loadKnownActions(); }}
        bankAccounts={bankAccounts}
        accountingCategories={accountingCategories}
        companyId={activeCompanyId ?? ''}
      />
    </div>
  );
}

// Helper function to get Hebrew label for cashflow mapping
function getCashflowMappingLabel(mapping: CashflowMapping): string {
  const labels: Record<CashflowMapping, string> = {
    current_business: 'שוטף - פעילות עסקית',
    financing_loan: 'מימון - פעילות הלוואה',
    intra_group_loan: 'הלוואה בתוך הקבוצה',
    investment: 'השקעה',
    savings: 'חיסכון',
    taxes: 'מיסים',
    inter_account_transfer: 'העברות בין חשבונות החברה',
    personal: 'אישי',
    other: 'אחר',
  };
  return labels[mapping] || mapping;
}
